import {
  getPrimaryColor,
  JEJURO,
  useApplyReferralCode,
} from '@orcar/common-d2c';
import { useState } from 'react';
import styled from 'styled-components';
import { trackReferralCodeSubmitted } from '@/utils/analytics';

type ReferralCodeAreaProps = {
  /** 현재 적용 가능한 추천 할인액(원). 0보다 크면 이미 적용 상태. */
  discount: number;
  /** 코드 적용 성공 후 할인 재조회 트리거 */
  onApplied: () => void;
};

const ReferralCodeArea = ({ discount, onApplied }: ReferralCodeAreaProps) => {
  const [input, setInput] = useState('');
  const applyReferral = useApplyReferralCode();

  const handleApply = () => {
    const code = input.trim().toUpperCase();
    if (!code) return;

    applyReferral.mutate(
      { code },
      {
        onSuccess: () => {
          trackReferralCodeSubmitted({
            referral_code: code,
            referral_source: 'checkout_input',
          });
          setInput('');
          onApplied();
        },
        onError: () => {
          alert('사용할 수 없는 추천코드예요. 코드를 다시 확인해주세요.');
        },
      },
    );
  };

  return (
    <Wrapper>
      <Header>추천인 코드</Header>

      {discount > 0 ? (
        <Applied>
          친구추천 할인 <b>{discount.toLocaleString()}원</b> 적용됨
        </Applied>
      ) : (
        <Row>
          <Input
            placeholder='추천인 코드 입력'
            value={input}
            onChange={(e) =>
              setInput(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))
            }
          />
          <ApplyButton
            type='button'
            onClick={handleApply}
            disabled={applyReferral.isPending}
          >
            적용
          </ApplyButton>
        </Row>
      )}

      <Hint>
        친구 추천코드를 입력하면 할인율에 따라 첫 예약 할인(최대 5,000원)을
        받아요. 제주 법 개정으로 차량 할인은 최대 60%까지 적용돼요.
      </Hint>
    </Wrapper>
  );
};

export default ReferralCodeArea;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
`;

const Header = styled.p`
  margin: 0;
  ${({ theme }) => theme.FONT.B1};
  font-weight: 700;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  height: 44px;
  padding: 0 12px;
  border: 1px solid ${({ theme }) => theme.COLOR.GREY_04};
  border-radius: 8px;
  text-transform: uppercase;
  ${({ theme }) => theme.FONT.B2};

  &:focus {
    outline: none;
    border-color: ${getPrimaryColor(JEJURO)};
  }
`;

const ApplyButton = styled.button`
  flex-shrink: 0;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.COLOR.WHITE};
  background: ${getPrimaryColor(JEJURO)};
  cursor: pointer;
  ${({ theme }) => theme.FONT.B2};
  font-weight: 700;

  &:disabled {
    opacity: 0.6;
  }
`;

const Applied = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 14px;
  border-radius: 8px;
  background: ${({ theme }) => theme.COLOR.GREY_BG};
  ${({ theme }) => theme.FONT.B2};
  font-weight: 600;

  b {
    margin: 0 4px;
    color: ${getPrimaryColor(JEJURO)};
    font-weight: 800;
  }
`;

const Hint = styled.p`
  margin: 0;
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_03};
`;
